import React, { useEffect, useRef } from "react";

/**
 * Reusable Shopify Buy Button component
 * @param {string} productId - Shopify product ID (numeric part only)
 * @param {string} buttonText - Text to display on button (default: "Add to Cart")
 * @param {string} destination - "cart" or "checkout" (default: "cart")
 */
const ShopifyBuyButton = ({ productId, buttonText = "Add to Cart", destination = "cart", showOptions = true }) => {
  const containerRef = useRef(null);
  const containerIdRef = useRef(`shopify-buy-${productId}-${destination}-${Date.now()}`);

  useEffect(() => {
    const scriptURL =
      "https://sdks.shopifycdn.com/buy-button/latest/buy-button-storefront.min.js";

    const loadScript = () => {
      return new Promise((resolve, reject) => {
        if (window.ShopifyBuy?.UI?.onReady) {
          resolve();
          return;
        }

        const existingScript = document.querySelector(`script[src="${scriptURL}"]`);
        
        const waitForUI = () => {
          let attempts = 0;
          const maxAttempts = 50;
          const check = () => {
            if (window.ShopifyBuy?.UI?.onReady) {
              resolve();
            } else if (attempts < maxAttempts) {
              attempts++;
              setTimeout(check, 100);
            } else {
              reject(new Error("Shopify Buy Button SDK failed to initialize"));
            }
          };
          check();
        };

        if (existingScript) {
          waitForUI();
          return;
        }

        const script = document.createElement("script");
        script.async = true;
        script.src = scriptURL;
        script.onload = waitForUI;
        script.onerror = () => reject(new Error("Failed to load Shopify SDK"));
        document.head.appendChild(script);
      });
    };

    const initShopifyBuy = async () => {
      try {
        await loadScript();
      } catch (err) {
        console.error("ShopifyBuyButton:", err.message);
        return;
      }
      
      const client = window.ShopifyBuy.buildClient({
        domain: "8f932e.myshopify.com",
        storefrontAccessToken: "302e2b9fd2581f32422c9edf466ec849",
      });

      window.ShopifyBuy.UI.onReady(client).then((ui) => {
        const container = document.getElementById(containerIdRef.current);
        if (!container) return;
        
        container.innerHTML = '';
        
        // Create the product button
        ui.createComponent("product", {
          id: productId,
          node: container,
          moneyFormat: "%24%7B%7Bamount%7D%7D",
          options: {
            product: {
              styles: {
                product: {
                  "@media (min-width: 601px)": {
                    maxWidth: "100%",
                    marginLeft: "0",
                    marginBottom: "0",
                  },
                },
                button: {
                  "font-size": "16px",
                  "padding-top": "16px",
                  "padding-bottom": "16px",
                  "padding-left": "40px",
                  "padding-right": "40px",
                  ":hover": {
                    "background-color": destination === "checkout" ? "#d4a017" : "#374151",
                  },
                  "background-color": destination === "checkout" ? "#eab308" : "#0f0f0f",
                  "color": destination === "checkout" ? "#0f0f0f" : "#ffffff",
                  ":focus": {
                    "background-color": destination === "checkout" ? "#d4a017" : "#374151",
                  },
                  "border-radius": "8px",
                  "font-weight": "bold",
                },
                title: { display: "none" },
                price: { display: "none" },
                compareAt: { display: "none" },
                description: { display: "none" },
              },
              contents: {
                img: false,
                title: false,
                price: false,
                description: false,
                button: true,
                quantity: false,
                options: showOptions,
              },
              buttonDestination: destination,
              text: {
                button: buttonText,
              },
            },
            cart: {
              styles: {
                button: {
                  "background-color": "#eab308",
                  "color": "#0f0f0f",
                  ":hover": {
                    "background-color": "#d4a017",
                  },
                  "border-radius": "8px",
                  "font-weight": "bold",
                },
              },
              text: {
                total: "Subtotal",
                button: "Checkout",
              },
              popup: false,
            },
            toggle: {
              styles: {
                toggle: {
                  "background-color": "#eab308",
                  ":hover": {
                    "background-color": "#d4a017",
                  },
                },
                count: {
                  "color": "#0f0f0f",
                },
              },
            },
          },
        });
      });
    };

    initShopifyBuy();

    return () => {
      const container = document.getElementById(containerIdRef.current);
      if (container) {
        container.innerHTML = '';
      }
    };
  }, [productId, buttonText, destination, showOptions]);

  return (
    <div
      ref={containerRef}
      id={containerIdRef.current}
      style={{ width: "100%", minHeight: "50px" }}
    />
  );
};

export default ShopifyBuyButton;
